
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import type { Company } from '../hooks/useCompanyManagement';

interface CompanyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<Company, 'id' | 'member_count' | 'customer_count' | 'admin_count'>) => Promise<boolean>;
  company?: Company | null;
  title: string;
  description: string;
}

export default function CompanyDialog({
  isOpen,
  onClose,
  onSave,
  company,
  title,
  description
}: CompanyDialogProps) {
  const [formData, setFormData] = useState({
    name: company?.name || '',
    email: company?.email || '',
    website: company?.website || '',
    address: company?.address || ''
  });

  const handleSave = async () => {
    const success = await onSave(formData);
    if (success) {
      setFormData({ name: '', email: '', website: '', address: '' });
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', website: '', address: '' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Company Name</label>
            <Input 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <Input 
              value={formData.email} 
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Website</label>
            <Input 
              value={formData.website} 
              onChange={(e) => setFormData({...formData, website: e.target.value})}
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Address</label>
            <Input 
              value={formData.address} 
              onChange={(e) => setFormData({...formData, address: e.target.value})}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {company ? 'Save changes' : 'Create company'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
